import { CSSProperties, Dispatch, FC } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router';
import classNames from 'classnames';
import { Action } from 'redux';

import lang from '../../lib/language';
import { AuthLoginData } from '../../lib/models/auth/types';
import { autoLogin as autoLoginCall } from '../../lib/store/common-actions';

import { useIsMobile } from '../../hooks';
import useBrowserWidth from '../../hooks/useBrowserWidth';
import Button from '../../shared_components/Button/Button';
import { ButtonTypesEnum } from '../../shared_components/Button/Button.types';
import { MAX_TABLET_WIDTH } from '../../util/constants';
import { UI_ROUTES } from '../../util/routes';
import { BaseModal } from '../index';

import styles from './PaymentCancelModal.module.scss';

type OwnProps = {
  isFromEnroll?: boolean;
  closeModal?: () => void;
};

type Props = OwnProps & ReduxProps;

const modalWidth = 466;

const PaymentCancelModal: FC<Props> = (props: Props): JSX.Element => {
  const { isFromEnroll, autoLogin, closeModal } = props;
  const { browserWidthSize } = useBrowserWidth();
  const { isMobile } = useIsMobile();
  const isMobileOrTablet = isMobile || browserWidthSize < MAX_TABLET_WIDTH;
  const navigate = useNavigate();

  const handleContinuePlatformOrCancell = (): void => {
    if (closeModal) {
      closeModal();
    }

    sessionStorage.removeItem('paymentGatewayURL');

    if (isFromEnroll) {
      // The session is already valid at this point, so autoLogin resolves without navigating.
      // Take the user to the platform and flag that the plan payment step was skipped, so the
      // app does not send them back to the enrollment payment step.
      autoLogin({} as any);
      navigate(UI_ROUTES.overview, { state: { skipPaymentEnrollment: true }, replace: true });
      return;
    }

    navigate(-1);
  };

  const modalStyle: CSSProperties = isMobileOrTablet
    ? {
      paddingBottom: 0,
      margin: 0,
      width: window.innerWidth,
      position: 'fixed',
      alignSelf: 'flex-end',
      left: 0,
      right: 0,
      maxWidth: '100vw',
      top: 'auto',
      bottom: 0,
    }
    : {
      width: modalWidth,
      position: 'fixed',
      left: `calc(50% - ${modalWidth / 2}px)`,
      top: '0',
    };

  return (
    <BaseModal customStyle={modalStyle} className={styles.modal} isOpen toggle={closeModal}>
      <div className={classNames(styles.content, { [styles.contentSm]: !isFromEnroll })}>
        <span className={styles.title}>
          {isFromEnroll
            ? lang.payamentMethodContinueWithoutPayment()
            : lang.payamentMethodCancellingPayment()}
        </span>
        {isFromEnroll && (
          <span className={styles.descriptipon}>
            {lang.payamentMethodContinueWithoutPaymentDesc()}
          </span>
        )}
      </div>
      <Button className={styles.button} variant={ButtonTypesEnum.Primary} onClick={closeModal}>
        {lang.payamentMethodProceedWithPayment()}
      </Button>
      <Button
        className={isMobileOrTablet ? styles.buttonCancelPayment : undefined}
        variant={ButtonTypesEnum.Link}
        onClick={handleContinuePlatformOrCancell}
      >
        {isFromEnroll
          ? lang.payamentMethodContinueWithoutPaymentBtn()
          : lang.payamentMethodCancelPaymentBtn()}
      </Button>
    </BaseModal>
  );
};

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  autoLogin: (data: AuthLoginData): void => dispatch(autoLoginCall(data)),
});

const connector = connect(null, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(PaymentCancelModal);
